import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChange, signIn, signUp, signOutUser, getCurrentUser } from '../firebase/auth';
import { createUser } from '../firebase/firestore';
import EmailVerificationPending from '../components/EmailVerificationPending';
import EmailVerificationSuccess from '../components/EmailVerificationSuccess';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [showEmailVerificationSuccess, setShowEmailVerificationSuccess] = useState(false);
  const [pendingUserEmail, setPendingUserEmail] = useState('');

  useEffect(() => {
    // Listen to Firebase Auth state changes
    const unsubscribe = onAuthStateChange(async (firebaseUser) => {
      if (firebaseUser) {
        // User is signed in
        
        // Check if email is verified
        if (!firebaseUser.emailVerified) {
          setShowEmailVerification(true);
          setPendingUserEmail(firebaseUser.email);
          setUser(null);
          setIsLoggedIn(false);
          setLoading(false);
          return;
        }
        
        try {
          // Removed delay that might be causing modal flashing issues
          
          // Get user data from backend API
          try {
            // Determine user role based on email (fallback, will be updated from backend)
            const isAdmin = firebaseUser.email === 'hello@studiogearcompare.com';
            const isBrand = firebaseUser.email === 'brandtest@hardwarecompare.com';
            const defaultRole = isAdmin ? 'admin' : (isBrand ? 'Brand' : 'user');
            
            // SECURITY FIX: Use Firebase ID token instead of JSON.stringify
            // Force refresh token to get latest custom claims
            const authToken = await firebaseUser.getIdToken(true);
            console.log('🔍 UserContext: Fetching user data from backend for Firebase UID:', firebaseUser.uid);
            const response = await fetch('/api/users/current', {
              headers: {
                'Authorization': `Bearer ${authToken}`
              }
            });
            
            if (response.ok) {
              const result = await response.json();
              if (result.success) {
                console.log('✅ UserContext: User data fetched successfully:', {
                  id: result.data.id,
                  email: result.data.email,
                  role: result.data.role
                });
                const userData = {
                  ...result.data,
                  uid: firebaseUser.uid,
                  email: firebaseUser.email,
                  displayName: firebaseUser.displayName,
                  emailVerified: firebaseUser.emailVerified
                };
                setUser(userData);
              } else {
                console.error('❌ UserContext: Backend returned error:', result.error);
                throw new Error(result.error || 'Failed to fetch user data');
              }
            } else {
              const errorText = await response.text();
              console.error('❌ UserContext: Backend API error:', response.status, errorText);
              throw new Error('Failed to fetch user data from backend');
            }
          } catch (error) {
            // Backend API access failed, using local user data
            
            // Determine user role based on email
            const isAdmin = firebaseUser.email === 'hello@studiogearcompare.com';
            const isBrand = firebaseUser.email === 'brandtest@hardwarecompare.com';
            
            // Get email marketing preference from sessionStorage
            let emailMarketingOptIn = false;
            try {
              const storedPreference = sessionStorage.getItem('emailMarketingOptIn');
              if (storedPreference) {
                emailMarketingOptIn = JSON.parse(storedPreference);
                // Clear it after use
                sessionStorage.removeItem('emailMarketingOptIn');
              }
            } catch (error) {
              console.warn('Error parsing email marketing preference:', error);
            }
            
            const userData = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName || firebaseUser.email.split('@')[0],
              role: isAdmin ? 'admin' : (isBrand ? 'Brand' : 'user'),
              tier: isAdmin ? 'premium' : 'free',
              preferences: {},
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              emailMarketingOptIn,
              emailVerified: firebaseUser.emailVerified,
              assigned_brand: isBrand ? 'Universal Audio' : null
            };
            
            // Setting user data locally
            setUser(userData);
          }
          setIsLoggedIn(true);
          setShowEmailVerification(false);
        } catch (error) {
          console.error('Error fetching user data:', error);
          setUser(null);
          setIsLoggedIn(false);
        }
      } else {
        // User is signed out
        setUser(null);
        setIsLoggedIn(false);
        setShowEmailVerification(false);
        setPendingUserEmail('');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      // Get current Firebase user to create auth token
      const firebaseUser = await new Promise((resolve) => {
        const unsubscribe = onAuthStateChange((user) => {
          unsubscribe();
          resolve(user);
        });
      });

      if (!firebaseUser) {
        console.log('No Firebase user found for fetchCurrentUser');
        return { success: false };
      }

      // SECURITY FIX: Use Firebase ID token instead of JSON.stringify
      // Force refresh token to get latest custom claims
      const authToken = await firebaseUser.getIdToken(true);
      console.log('🔍 fetchCurrentUser: Fetching user data with token:', authToken);
      const response = await fetch('/api/users/current', {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 fetchCurrentUser: API response:', data);
        if (data.success) {
          const userData = {
            ...data.data,
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            emailVerified: firebaseUser.emailVerified
          };
          console.log('🔍 fetchCurrentUser: Setting user data:', userData);
          console.log('🔍 fetchCurrentUser: User role:', userData.role);
          setUser(userData);
          // Return success with user data
          return { success: true, user: userData, firebaseUser };
        }
      } else {
        console.error('Failed to fetch current user from API');
        return { success: false };
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    console.log('🔄 refreshUser: Forcing user data refresh');
    setLoading(true);
    try {
      await fetchCurrentUser();
      console.log('✅ refreshUser: User data refreshed successfully');
    } catch (error) {
      console.error('❌ refreshUser: Error refreshing user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const upgradeToPremium = async () => {
    try {
      // In a real app, this would integrate with a payment processor
      const response = await fetch('/api/users/1', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tier: 'premium' }),
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.data);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error upgrading to premium:', error);
      return false;
    }
  };

  const downgradeToFree = async () => {
    try {
      const response = await fetch('/api/users/1', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tier: 'free' }),
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.data);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error downgrading to free:', error);
      return false;
    }
  };

  const isPremium = () => {
    return user?.tier === 'premium' || user?.tier === 'user+';
  };

  const isFree = () => {
    return user?.tier === 'free' && user?.tier !== 'user+';
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const isAuthor = () => {
    return user?.role === 'author';
  };

  const isAdminOrAuthor = () => {
    return user?.role === 'admin' || user?.role === 'author';
  };

  const isUser = () => {
    return user?.role === 'user';
  };

  const isBrand = () => {
    // Check if user is a brand
    return user?.role === 'Brand';
  };

  const login = async (email, password) => {
    try {
      // Attempting login
      const result = await signIn(email, password);
      if (result.success) {
        // User state will be updated by the auth state listener
        return { success: true };
      } else {
        console.log('❌ UserContext: Login failed:', result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.log('❌ UserContext: Login error:', error);
      return { success: false, error: error.message };
    }
  };

  const loginWithGoogle = async () => {
    try {
      const { signInWithGoogle } = require('../firebase/auth');
      const result = await signInWithGoogle();
      if (result.success) {
        // User state will be updated by the auth state listener
        return { success: true };
      } else {
        console.log('❌ UserContext: Google login failed:', result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.log('❌ UserContext: Google login error:', error);
      return { success: false, error: error.message };
    }
  };

  const register = async (email, password, displayName, emailMarketingOptIn = false) => {
    try {
      const result = await signUp(email, password, displayName, emailMarketingOptIn);
      if (result.success) {
        // Store email marketing preference for backend user creation
        if (result.emailMarketingOptIn !== undefined) {
          localStorage.setItem('pendingEmailMarketingOptIn', JSON.stringify(result.emailMarketingOptIn));
          // Also store in sessionStorage for immediate use
          sessionStorage.setItem('emailMarketingOptIn', JSON.stringify(result.emailMarketingOptIn));
        }
        
        // IMPORTANT: Create user in Firestore immediately after sign-up
        // This ensures the user exists even if email verification happens in another tab
        try {
          const firebaseUser = getCurrentUser();
          if (firebaseUser) {
            console.log('🔍 UserContext: Creating user in Firestore immediately after sign-up for:', firebaseUser.uid);
            const authToken = await firebaseUser.getIdToken(true);
            const response = await fetch('/api/users/current', {
              headers: {
                'Authorization': `Bearer ${authToken}`
              }
            });
            
            if (response.ok) {
              const data = await response.json();
              if (data.success) {
                console.log('✅ UserContext: User created in Firestore during sign-up:', {
                  id: data.data.id,
                  email: data.data.email
                });
              } else {
                console.warn('⚠️ UserContext: User creation returned error (may already exist):', data.error);
              }
            } else {
              console.warn('⚠️ UserContext: Failed to create user during sign-up, will retry after email verification');
            }
          }
        } catch (createError) {
          console.warn('⚠️ UserContext: Error creating user during sign-up (will retry after email verification):', createError);
          // Don't fail sign-up if user creation fails - it will be retried after email verification
        }
        
        // User state will be updated by the auth state listener
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      const result = await signOutUser();
      if (result.success) {
        // User state will be updated by the auth state listener
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const handleEmailVerificationComplete = async () => {
    setShowEmailVerification(false);
    setPendingUserEmail('');
    // Force a reload of the auth state to pick up email verification
    // This will trigger the auth state listener to update the user
    try {
      const firebaseUser = getCurrentUser();
      if (firebaseUser) {
        // Reload user to get updated email verification status
        await firebaseUser.reload();
        
        // Force token refresh to get updated email verification status
        await firebaseUser.getIdToken(true);
        
        // Check if email is now verified
        if (firebaseUser.emailVerified) {
          console.log('🔍 Email verified, fetching user data...');
          
          // Fetch user data from backend directly (don't use fetchCurrentUser to avoid nested listener)
          const authToken = await firebaseUser.getIdToken(true);
          const response = await fetch('/api/users/current', {
            headers: {
              'Authorization': `Bearer ${authToken}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              const userData = {
                ...data.data,
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                displayName: firebaseUser.displayName,
                emailVerified: firebaseUser.emailVerified
              };
              
              // Manually set all state to ensure user is logged in immediately
              setUser(userData);
              setIsLoggedIn(true);
              setLoading(false);
              
              // Hide email verification screen and clear pending email
              setShowEmailVerification(false);
              setPendingUserEmail('');
              
              console.log('✅ User logged in after email verification:', {
                id: userData.id,
                email: userData.email,
                role: userData.role
              });
              
              // Hide email verification screen and show success screen
              setShowEmailVerification(false);
              setShowEmailVerificationSuccess(true);
              setPendingUserEmail('');
            } else {
              console.error('❌ Failed to fetch user data after email verification:', data.error);
            }
          } else {
            console.error('❌ API error after email verification:', response.status);
          }
        } else {
          console.warn('⚠️ Email not verified yet');
        }
      }
    } catch (error) {
      console.error('Error reloading user after email verification:', error);
      // Fallback to try fetching user data even if reload fails
      try {
        const firebaseUser = getCurrentUser();
        if (firebaseUser) {
          const authToken = await firebaseUser.getIdToken(true);
          const response = await fetch('/api/users/current', {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              const userData = { ...data.data, uid: firebaseUser.uid, email: firebaseUser.email, displayName: firebaseUser.displayName, emailVerified: firebaseUser.emailVerified };
              setUser(userData);
              setIsLoggedIn(true);
              setLoading(false);
              setShowEmailVerification(false);
              setShowEmailVerificationSuccess(true);
              setPendingUserEmail('');
            }
          }
        }
      } catch (fetchError) {
        console.error('Error fetching user data after email verification (fallback):', fetchError);
      }
    }
  };

  const handleEmailVerificationSuccessContinue = () => {
    setShowEmailVerificationSuccess(false);
    // Trigger auth state listener to ensure everything is synced
    // The user is already logged in, so this should just refresh the UI
    window.location.reload();
  };

  const value = {
    user,
    loading,
    isLoggedIn,
    isPremium,
    isFree,
    isAdmin,
    isAuthor,
    isAdminOrAuthor,
    isUser,
    isBrand,
    login,
    loginWithGoogle,
    register,
    logout,
    upgradeToPremium,
    downgradeToFree,
    fetchCurrentUser,
    refreshUser
  };

  // Show email verification screen if needed
  if (showEmailVerificationSuccess) {
    return (
      <EmailVerificationSuccess
        onContinue={handleEmailVerificationSuccessContinue}
      />
    );
  }

  if (showEmailVerification) {
    return (
      <EmailVerificationPending
        userEmail={pendingUserEmail}
        onVerificationComplete={handleEmailVerificationComplete}
      />
    );
  }

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
